/**
 * What survives a hardcopy page rotation, decided over serialized annotations rather than live
 * Fabric objects.
 *
 * Rotating a page rebuilds the overlay canvas at the turned size, which means every annotation is
 * torn down and rehydrated from JSON. Identity therefore has to travel *in* the serialized data:
 * a held Fabric reference outlives the object it points at, so tracking one lets a deleted note
 * come back and lets an undone note be carried and re-placed at the same time.
 *
 * Pure, so the decision can be unit tested without fabric, pdf.js or pdf-lib present.
 */

import { rotatePlacementForPageTurn } from './pdfPageGeometry';

/** How an annotation object relates to the note it belongs to. */
export type AnnotationRole = 'note' | 'stamp' | 'background';

/**
 * The custom properties that must be named on every `toObject`/`toJSON` call, because fabric
 * serializes only the properties it declares. Anything missing here silently disappears on the next
 * `loadFromJSON` — which is exactly how note identity used to be lost.
 */
export const ANNOTATION_EXPORT_PROPS: string[] = [
    'annotationId',
    'annotationRole',
    'linkedAnnotationId',
    'userPositioned',
    'stampOffsetY',
];

/** A serialized Fabric object, with the annotation properties this module reads named explicitly. */
export interface SerializedAnnotation {
    left?: number;
    top?: number;
    angle?: number;
    text?: string;
    annotationId?: string;
    annotationRole?: AnnotationRole;
    /** Set on a stamp or background rect: the `annotationId` of the note it belongs to. */
    linkedAnnotationId?: string;
    /** Set once the user has dragged or resized the note, making its placement theirs. */
    userPositioned?: boolean;
    excludeFromExport?: boolean;
    [key: string]: unknown;
}

export interface PageTurnOptions {
    /** Quarter turn being applied, clockwise; negatives are normalized. */
    turnDegrees: number;
    /** Overlay dimensions BEFORE the turn. */
    displayWidth: number;
    displayHeight: number;
    /** `annotationId` of the note auto-placement put on this page, or null if there is none. */
    autoPlacedNoteId: string | null;
}

export interface PageTurnPlan {
    /** Annotations to restore on the turned page, already re-placed into the new orientation. */
    carried: SerializedAnnotation[];
    /** Text of the auto-placed note to place afresh, or null when there is nothing to re-place. */
    replacedNoteText: string | null;
}

/**
 * The auto-placed note itself, out of a page's serialized objects.
 *
 * Every consumer that has to answer "which of these is the note" asks here, so the burn, the page
 * turn and the recorded note text all resolve to the same object. Stating the test twice is how the
 * recorded text and the burned text drift apart, and text recorded that the hardcopy does not say is
 * the one outcome worse than recording nothing at all.
 *
 * Three things have to hold. The object claims the role, so a stamp or backing rect cannot stand in
 * for the note it belongs to. It carries the id auto-placement assigned, so a note the user typed
 * by hand is not mistaken for the placed one. And it is exported, so anything the burn drops is
 * never recorded as though it had been burned.
 */
export function findAutoPlacedNote(
    objects: SerializedAnnotation[],
    autoPlacedNoteId: string | null | undefined
): SerializedAnnotation | undefined {
    if (!autoPlacedNoteId) return undefined;

    return objects.find(
        (obj) => obj.annotationRole === 'note'
            && obj.annotationId === autoPlacedNoteId
            && !obj.linkedAnnotationId
            && obj.excludeFromExport !== true
    );
}

/** Is this object the note itself, or the stamp/background belonging to it? */
function belongsToNote(obj: SerializedAnnotation, note: SerializedAnnotation | undefined): boolean {
    if (!note) return false;
    if (obj === note) return true;

    return !!note.annotationId && obj.linkedAnnotationId === note.annotationId;
}

/**
 * Decide what a page rotation carries and what it re-places.
 *
 * Everything the user put on the page follows the page, so it stays where they put it relative to
 * the hardcopy. The single exception is a note that auto-placement put down and the user has not
 * touched: it was aimed at blank space, and the space it was aimed at is generally not blank once
 * the page turns, so it is withheld here and placed afresh against the new orientation.
 *
 * A note the user has dragged or resized is theirs, and is carried like any other annotation.
 */
export function planPageTurn(
    objects: SerializedAnnotation[],
    options: PageTurnOptions
): PageTurnPlan {
    const { turnDegrees, displayWidth, displayHeight, autoPlacedNoteId } = options;

    const trackedNote = findAutoPlacedNote(objects, autoPlacedNoteId);

    // No match means the note is gone — deleted, or cropped away. There is nothing to re-place, and
    // re-placing it anyway would burn a note the user removed into a flattened image.
    const replacedNote = trackedNote && trackedNote.userPositioned !== true ? trackedNote : undefined;

    const carried = objects
        // Crop overlays and other transient UI are never part of the saved page.
        .filter((obj) => obj.excludeFromExport !== true)
        .filter((obj) => !belongsToNote(obj, replacedNote))
        .map((obj) => ({
            ...obj,
            ...rotatePlacementForPageTurn(obj, turnDegrees, displayWidth, displayHeight),
        }));

    const replacedText = typeof replacedNote?.text === 'string' ? replacedNote.text : null;

    return {
        carried,
        // An emptied note has nothing worth re-placing.
        replacedNoteText: replacedText && replacedText.trim() ? replacedText : null,
    };
}
